import { useTypedSelector } from "@/store/reducers/use-typed-selector";
import { SurveyQuestion } from "@/types/working-model";
import { matchSurveyResponseKey } from "@/utils/regex";
import { useEffect, useMemo, useState } from "react";

const useSurveyResponseCount = ({ questions }: { questions?: SurveyQuestion[]; }) => {
	const engagementResults = useTypedSelector(state => state.SurveysReducer.engagementResults);
	const textResults = useTypedSelector(state => state.SurveysReducer.textResults);
	const [totalResponses, setTotalResponses] = useState(0);
	const [questionResults, setQuestionResults] = useState<Record<number, Record<number, number>>>({});

	const questionIds = useMemo(
		() => questions?.map(q => q.survey_question) ?? [],
		// rerun based on joing question ids - do not undo this memo or we'll get infinite loops
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[questions?.map(q => q.survey_question).join(',')]
	);

	useEffect(() => {
		if (!questionIds.length) return;

		let _totalResponses = 0;
		const _questionResults: Record<number, Record<number, number>> = {};

		// option-based responses
		for (const [resultKey, value] of Object.entries(engagementResults ?? {})) {
			const match = matchSurveyResponseKey(resultKey);
			if (match) {
				const { survey_question: sq, option } = match;
				// skip text entries when textResults has data for this question to avoid double-counting
				if (option === 'text' && textResults[Number(sq)]) continue;
				if (questionIds.includes(Number(sq))) {
					_totalResponses += value;
					if (!_questionResults[Number(sq)]) _questionResults[Number(sq)] = {};
					_questionResults[Number(sq)][Number(option)] = value;
				}
			}
		}

		// text-based responses
		for (const qId of questionIds) {
			if (textResults[qId]) {
				_totalResponses += textResults[qId].total;
			}
		}

		setTotalResponses(_totalResponses);
		setQuestionResults(_questionResults);
	}, [engagementResults, questionIds, textResults]);

	return { totalResponses, questionResults };
};

export default useSurveyResponseCount;